package co.maytes.standupbot.dashboard.features.releases

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.ControlSize
import co.maytes.standupbot.dashboard.components.widgets.Popover
import co.maytes.standupbot.dashboard.components.widgets.PopoverPlacement
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideChevronDown
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private const val MAX_REPO_CHIPS = 3
private const val SMALL_ICON = 12
private const val MEDIUM_ICON = 16
private const val BAR_HEIGHT_VAR = "--sb-rel-bar-h"
private const val OPTION_LIST_MAX_HEIGHT = "18rem"

val zoneOptions = ReleaseZoneChoice.entries.map { SegmentedOption(it, it.label) }
private val rangeOptions = RangeFilter.entries.map { SegmentedOption(it, it.label) }
private val groupingOptions = FeedGrouping.entries.map { SegmentedOption(it, it.label) }
private val densityOptions = FeedDensity.entries.map { SegmentedOption(it, it.label) }

data class FeedBarState(
    val filters: ReleaseFilters,
    val kindCounts: KindCounts?,
    val repoOptions: List<RepoOption>,
    val grouping: FeedGrouping,
    val density: FeedDensity,
    val zoneChoice: ReleaseZoneChoice,
    val resultText: String?,
    val partialNote: String?,
)

data class FeedBarActions(
    val onFilters: (ReleaseFilters) -> Unit,
    val onGrouping: (FeedGrouping) -> Unit,
    val onDensity: (FeedDensity) -> Unit,
    val onZone: (ReleaseZoneChoice) -> Unit,
)

@Composable
fun ReleaseFeedBar(
    state: FeedBarState,
    actions: FeedBarActions,
) {
    var filtersOpen by remember { mutableStateOf(false) }
    val filters = state.filters
    Div(
        attrs =
            Modifier.classNames(FeedClasses.BAR).toAttrs {
                ref { element ->
                    val stop = observeBarHeight(element)
                    onDispose { stop() }
                }
            },
    ) {
        Div(attrs = Modifier.classNames(FeedClasses.FILTERS_ROW).toAttrs()) {
            Box(modifier = Modifier.classNames(FeedClasses.SEARCH)) {
                TextInput(
                    value = filters.query,
                    onValueChange = { actions.onFilters(filters.copy(query = it)) },
                    placeholder = "Search changes",
                    ariaLabel = "Search changes",
                    size = ControlSize.Md,
                )
            }
            Div(attrs = Modifier.classNames(FeedClasses.FILTER_TOGGLE).toAttrs()) {
                Button(
                    label = if (filters.isActive) "Filters (${activeFilterCount(filters)})" else "Filters",
                    variant = ButtonVariant.Secondary,
                    modifier =
                        Modifier.attrsModifier {
                            attr("aria-expanded", filtersOpen.toString())
                            attr("aria-controls", FILTERS_ID)
                        },
                    leadingIcon = { FilterSlidersIcon() },
                    onClick = { filtersOpen = !filtersOpen },
                )
            }
            Div(
                attrs =
                    Modifier.classNames(FeedClasses.FILTERS).toAttrs {
                        attr("id", FILTERS_ID)
                        attr("data-open", filtersOpen.toString())
                    },
            ) {
                SegmentedControl(
                    options = KindFilter.entries.map { SegmentedOption(it, it.label, state.kindCounts?.of(it)) },
                    selected = filters.kind,
                    onSelect = { actions.onFilters(filters.copy(kind = it)) },
                    ariaLabel = "Type of change",
                )
                SegmentedControl(
                    options = rangeOptions,
                    selected = filters.range,
                    onSelect = { actions.onFilters(filters.copy(range = it)) },
                    ariaLabel = "Time range",
                )
                RepoPicker(
                    options = state.repoOptions,
                    selected = filters.repos,
                    onChange = { actions.onFilters(filters.copy(repos = it)) },
                )
            }
        }
        Div(attrs = Modifier.classNames(FeedClasses.SUMMARY_ROW).toAttrs()) {
            Div(attrs = Modifier.classNames(FeedClasses.SUMMARY).toAttrs()) {
                state.resultText?.let { ResultText(it) }
                ActiveFilterChips(filters, actions.onFilters)
            }
            Div(attrs = Modifier.classNames(FeedClasses.VIEW_CONTROLS).toAttrs()) {
                SegmentedControl(
                    options = groupingOptions,
                    selected = state.grouping,
                    onSelect = actions.onGrouping,
                    ariaLabel = "Group by",
                )
                ViewMenu(state.density, actions.onDensity, state.zoneChoice, actions.onZone)
            }
        }
        state.partialNote?.let {
            SpanText(
                text = it,
                modifier = Modifier.margin(top = Spacing.xxs).fontSize(Typography.caption.size).color(Palette.textTertiary),
            )
        }
    }
}

private const val FILTERS_ID = "sb-rel-filters"

internal fun activeFilterCount(filters: ReleaseFilters): Int =
    listOf(
        filters.query.isNotBlank(),
        filters.kind != KindFilter.All,
        filters.range != RangeFilter.All,
        filters.repos.isNotEmpty(),
    ).count { it }

@Composable
private fun ResultText(text: String) {
    SpanText(
        text = text,
        modifier =
            Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(Typography.label.weight)
                .color(Palette.textSecondary)
                .styleModifier { property("font-variant-numeric", "tabular-nums") }
                .attrsModifier {
                    attr("role", "status")
                    attr("aria-live", "polite")
                },
    )
}

@Composable
private fun ActiveFilterChips(
    filters: ReleaseFilters,
    onFilters: (ReleaseFilters) -> Unit,
) {
    if (!filters.isActive) return
    if (filters.query.isNotBlank()) FilterChip("Search: ${filters.query.trim()}") { onFilters(filters.copy(query = "")) }
    if (filters.kind != KindFilter.All) FilterChip(filters.kind.label) { onFilters(filters.copy(kind = KindFilter.All)) }
    if (filters.range != RangeFilter.All) FilterChip("Last ${filters.range.label}") { onFilters(filters.copy(range = RangeFilter.All)) }
    val repos = filters.repos.sorted()
    repos.take(MAX_REPO_CHIPS).forEach { repo -> FilterChip(repo) { onFilters(filters.copy(repos = filters.repos - repo)) } }
    if (repos.size > MAX_REPO_CHIPS) {
        FilterChip("+${repos.size - MAX_REPO_CHIPS} more") { onFilters(filters.copy(repos = repos.take(MAX_REPO_CHIPS).toSet())) }
    }
    Button(
        label = "Clear all",
        variant = ButtonVariant.Ghost,
        size = ButtonSize.Sm,
        onClick = { onFilters(ReleaseFilters()) },
    )
}

@Composable
private fun FilterChip(
    label: String,
    onRemove: () -> Unit,
) {
    HtmlButton(
        attrs =
            Modifier.classNames(FeedClasses.CHIP).onClick { onRemove() }.toAttrs {
                attr("type", "button")
                attr("aria-label", "Remove filter: $label")
            },
    ) {
        SpanText(
            text = label,
            modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textPrimary).singleLine(),
        )
        Box(modifier = Modifier.color(Palette.textTertiary).styleModifier { property("display", "inline-flex") }) {
            LucideX(size = SMALL_ICON)
        }
    }
}

@Composable
private fun RepoPicker(
    options: List<RepoOption>,
    selected: Set<String>,
    onChange: (Set<String>) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    var search by remember { mutableStateOf("") }
    val visible = options.filter { search.isBlank() || it.repo.contains(search.trim(), ignoreCase = true) }
    Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
        Button(
            label = if (selected.isEmpty()) "All repositories" else "Repositories (${selected.size})",
            variant = ButtonVariant.Secondary,
            modifier =
                Modifier.attrsModifier {
                    attr("aria-haspopup", "listbox")
                    attr("aria-expanded", open.toString())
                },
            trailingIcon = { LucideChevronDown(size = MEDIUM_ICON) },
            onClick = { open = !open },
        )
        Popover(
            open = open,
            placement = PopoverPlacement.BottomStart,
            onDismiss = {
                open = false
                search = ""
            },
            modifier = Modifier.classNames(FeedClasses.POPOVER),
            ariaLabel = "Filter by repository",
        ) {
            Div(attrs = Modifier.styleModifier { property("display", "grid") }.gap(Spacing.xs).toAttrs()) {
                TextInput(
                    value = search,
                    onValueChange = { search = it },
                    placeholder = "Find a repository",
                    ariaLabel = "Find a repository",
                    size = ControlSize.Sm,
                )
                Div(
                    attrs =
                        Modifier
                            .styleModifier {
                                property("max-height", OPTION_LIST_MAX_HEIGHT)
                                property("overflow-y", "auto")
                            }
                            .toAttrs {
                                attr("role", "listbox")
                                attr("aria-multiselectable", "true")
                                attr("aria-label", "Repositories")
                            },
                ) {
                    if (visible.isEmpty()) {
                        SpanText(
                            text = if (options.isEmpty()) "No repositories loaded yet" else "No repositories match",
                            modifier =
                                Modifier
                                    .margin(topBottom = Spacing.xs, leftRight = Spacing.sm)
                                    .fontSize(Typography.bodySmall.size)
                                    .color(Palette.textTertiary)
                                    .styleModifier { property("display", "block") },
                        )
                    }
                    visible.forEach { option ->
                        val isSelected = option.repo in selected
                        RepoOptionRow(option, isSelected) {
                            onChange(if (isSelected) selected - option.repo else selected + option.repo)
                        }
                    }
                }
                Div(
                    attrs =
                        Modifier
                            .styleModifier {
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "space-between")
                            }
                            .margin(leftRight = Spacing.sm)
                            .toAttrs(),
                ) {
                    SpanText(
                        text = if (selected.isEmpty()) "None selected" else "${selected.size} selected",
                        modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
                    )
                    Button(
                        label = "Clear",
                        variant = ButtonVariant.Ghost,
                        size = ButtonSize.Sm,
                        enabled = selected.isNotEmpty(),
                        onClick = { onChange(emptySet()) },
                    )
                }
            }
        }
    }
}

@Composable
private fun RepoOptionRow(
    option: RepoOption,
    selected: Boolean,
    onToggle: () -> Unit,
) {
    HtmlButton(
        attrs =
            Modifier.classNames(FeedClasses.OPTION).onClick { onToggle() }.toAttrs {
                attr("type", "button")
                attr("role", "option")
                attr("aria-selected", selected.toString())
            },
    ) {
        Box(
            modifier =
                Modifier
                    .color(Palette.onPrimary)
                    .styleModifier {
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("flex-shrink", "0")
                        property("width", "${IconSize.md}")
                        property("height", "${IconSize.md}")
                        property("border-radius", "${Radius.sm}")
                        property("border", "1px solid ${(if (selected) Palette.primary else Palette.border).cssRgb()}")
                        property("background-color", if (selected) Palette.primary.cssRgb() else "transparent")
                    },
        ) {
            if (selected) LucideCheck(size = SMALL_ICON)
        }
        SpanText(
            text = option.repo,
            modifier =
                Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textPrimary)
                    .singleLine()
                    .styleModifier { property("flex", "1 1 auto") },
        )
        SpanText(
            text = option.count?.toString() ?: "–",
            modifier =
                Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier { property("font-variant-numeric", "tabular-nums") },
        )
    }
}

@Composable
private fun ViewMenu(
    density: FeedDensity,
    onDensity: (FeedDensity) -> Unit,
    zoneChoice: ReleaseZoneChoice,
    onZone: (ReleaseZoneChoice) -> Unit,
) {
    var open by remember { mutableStateOf(false) }
    Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
        Button(
            label = "View",
            variant = ButtonVariant.Ghost,
            modifier =
                Modifier.attrsModifier {
                    attr("aria-haspopup", "dialog")
                    attr("aria-expanded", open.toString())
                },
            trailingIcon = { LucideChevronDown(size = MEDIUM_ICON) },
            onClick = { open = !open },
        )
        Popover(
            open = open,
            placement = PopoverPlacement.BottomEnd,
            onDismiss = { open = false },
            modifier = Modifier.classNames(FeedClasses.POPOVER),
            role = "dialog",
            ariaLabel = "View options",
        ) {
            Div(attrs = Modifier.styleModifier { property("display", "grid") }.gap(Spacing.sm).margin(Spacing.xs).toAttrs()) {
                ViewOption("Density") {
                    SegmentedControl(densityOptions, density, onDensity, ariaLabel = "Density")
                }
                ViewOption("Time zone") {
                    SegmentedControl(zoneOptions, zoneChoice, onZone, ariaLabel = "Time zone")
                }
            }
        }
    }
}

@Composable
private fun ViewOption(
    label: String,
    content: @Composable () -> Unit,
) {
    Div(attrs = Modifier.styleModifier { property("display", "grid") }.gap(Spacing.xs).toAttrs()) {
        SpanText(
            text = label,
            modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
        )
        content()
    }
}

private fun observeBarHeight(element: HTMLElement): () -> Unit {
    val host = element.parentElement as? HTMLElement ?: return {}
    val update: (dynamic, dynamic) -> Unit = { _, _ ->
        host.style.setProperty(BAR_HEIGHT_VAR, "${element.offsetHeight}px")
    }
    update(null, null)
    val observer = js("new window.ResizeObserver(update)")
    observer.observe(element)
    return {
        observer.disconnect()
        host.style.removeProperty(BAR_HEIGHT_VAR)
    }
}
